import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  Search, 
  BookOpen, 
  Video, 
  FileText, 
  ArrowLeft, 
  Play, 
  Clock, 
  Award,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  GraduationCap,
  Info,
  X,
  Building,
  Shield,
  Calendar,
  Users,
  Sparkles
} from 'lucide-react';
import { api } from '../../../../../api/client';


import type { Perfil, Topico, Subtopico } from '../../types';
import { useEcadCatalog } from './hooks/useEcadCatalog';
import { TopicAccordion } from './components/TopicAccordion';
import { VideoPlayerModal } from './components/VideoPlayerModal';

const API = import.meta.env.VITE_API_URL || '/api/v1';

export function EcadPageModular() {
  const navigate = useNavigate();
  const { modulo } = useParams<{ modulo?: string }>();
  const [searchParams] = useSearchParams();
  const perfilId = searchParams.get('perfil');
  const topicoId = searchParams.get('topico');

  const {
    perfis,
    searchQuery,
    setSearchQuery,
    loading,
    showDetailsModal,
    setShowDetailsModal,
    activePerfil,
    isInterno,
    effectivePerfil,
    effectiveTopico,
    selectedSubtopico,
    hasPrev,
    hasNext,
    handlePrev,
    handleNext,
    searchResults,
  } = useEcadCatalog({
    modulo,
    perfilId,
    topicoId,
    navigate,
  });

  // Mapeamento de estilos visuais e descrições dos perfis
  const getPerfilStyles = (id: string) => {
    switch (id) {
      case 'prefeituras':
        return {
          icon: <Building size={24} />,
          color: '#3b82f6',
          bgLight: 'rgba(59, 130, 246, 0.1)',
          borderLight: 'rgba(59, 130, 246, 0.2)',
          desc: 'Acompanhamento macro, repasses e relatórios municipais.'
        };
      case 'diretores':
        return {
          icon: <Shield size={24} />,
          color: '#10b981',
          bgLight: 'rgba(16, 185, 129, 0.1)',
          borderLight: 'rgba(16, 185, 129, 0.2)',
          desc: 'Gestão escolar, ocorrências e mitigação de evasão escolar.'
        };
      case 'secretarias':
        return {
          icon: <FileText size={24} />,
          color: '#ec4899',
          bgLight: 'rgba(236, 72, 153, 0.1)',
          borderLight: 'rgba(236, 72, 153, 0.2)',
          desc: 'Matrículas, cadastro de alunos/docentes e documentação.'
        };
      case 'coordenadores':
        return {
          icon: <Calendar size={24} />,
          color: '#f59e0b',
          bgLight: 'rgba(245, 158, 11, 0.1)',
          borderLight: 'rgba(245, 158, 11, 0.2)',
          desc: 'Grade horária, atribuição de aulas e NOC de contingência.'
        };
      case 'professores':
        return {
          icon: <GraduationCap size={24} />,
          color: '#a78bfa',
          bgLight: 'rgba(167, 139, 250, 0.1)',
          borderLight: 'rgba(167, 139, 250, 0.2)',
          desc: 'Lançamento de chamadas diárias e notas pedagógicas.'
        };
      case 'responsaveis':
        return {
          icon: <Users size={24} />,
          color: '#06b6d4',
          bgLight: 'rgba(6, 182, 212, 0.1)',
          borderLight: 'rgba(6, 182, 212, 0.2)',
          desc: 'Frequência dos filhos, comunicados e justificativa de faltas.'
        };
      case 'alunos':
        return {
          icon: <BookOpen size={24} />,
          color: '#14b8a6',
          bgLight: 'rgba(20, 184, 166, 0.1)',
          borderLight: 'rgba(20, 184, 166, 0.2)',
          desc: 'Acesso ao boletim, tarefas e trilha gamificada de medalhas.'
        };
      default:
        return {
          icon: <GraduationCap size={24} />,
          color: '#8b5cf6',
          bgLight: 'rgba(139, 92, 246, 0.1)',
          borderLight: 'rgba(139, 92, 246, 0.2)',
          desc: 'Roteiros de capacitação unificados.'
        };
    }
  };

  const activeStyles = effectivePerfil ? getPerfilStyles(effectivePerfil.id) : getPerfilStyles('default');

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#020617',
        color: '#f8fafc',
        fontFamily: "'Plus Jakarta Sans', sans-serif"
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(167, 139, 250, 0.1)',
            borderTop: '4px solid #a78bfa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px auto'
          }} />
          <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>Carregando capacitações...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Renderização principal com divisão em 4 telas
  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      background: 'radial-gradient(circle at 50% 30%, #0f172a 0%, #020617 100%)',
      color: '#f8fafc',
      display: 'flex',
      flexDirection: 'row',
      fontFamily: "'Plus Jakarta Sans', sans-serif"
    }}>
      
      {/* Injeção de Estilos CSS Premium com Hovers Tridimensionais */}
      <style dangerouslySetInnerHTML={{ __html: `
        .premium-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .premium-card:hover {
          transform: translateY(-5px) scale(1.015) !important;
          background: rgba(255, 255, 255, 0.05) !important;
          border-color: rgba(255, 255, 255, 0.12) !important;
          box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(0, 0, 0, 0.4) !important;
        }
        .sidebar-item {
          transition: all 0.2s ease !important;
        }
        .sidebar-item:hover {
          background: rgba(255, 255, 255, 0.03) !important;
        }
        .back-btn {
          transition: all 0.2s ease !important;
        }
        .back-btn:hover {
          background: rgba(255, 255, 255, 0.06) !important;
          transform: translateX(-2px) !important;
        }
        .action-btn {
          transition: all 0.2s ease !important;
        }
        .action-btn:hover:not(:disabled) {
          transform: translateY(-1px) !important;
          filter: brightness(1.1) !important;
        }
      `}} />

      <>
        {isInterno && (
          <button
            onClick={() => navigate('/ecadstudio')}
              style={{
                position: 'absolute',
                top: '20px',
                right: '24px',
                zIndex: 100,
                background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
                border: 'none',
                borderRadius: '10px',
                color: '#ffffff',
                padding: '10px 16px',
                fontSize: '0.82rem',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
              }}
            >
              <Sparkles size={16} />
              Estúdio AI Studio
            </button>
          )}

      {/* CASO 4: TELA DO PLAYER COM SIDEBAR HIERÁRQUICA */}
      {selectedSubtopico && effectivePerfil ? (
        <>
          {/* Sidebar Hierárquica */}
          <aside style={{
            width: '320px',
            borderRight: '1px solid rgba(255, 255, 255, 0.06)',
            background: 'rgba(3, 7, 18, 0.4)',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            overflow: 'hidden'
          }}>
            {/* Sidebar Header */}
            <div style={{
              padding: '16px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
              background: 'rgba(15, 23, 42, 0.4)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button 
                  onClick={() => navigate(`/ecad?perfil=${effectivePerfil.id}&topico=${effectiveTopico?.id || ''}`)}
                  className="back-btn"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '8px',
                    padding: '6px',
                    color: '#f8fafc',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Voltar aos Subtópicos"
                >
                  <ArrowLeft size={16} />
                </button>
                <div>
                  <span style={{ 
                    fontSize: '0.62rem', 
                    color: activeStyles.color, 
                    fontWeight: 700, 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.5px' 
                  }}>
                    Capacitação {effectivePerfil.nome}
                  </span>
                  <h1 style={{
                    fontSize: '1.05rem',
                    fontWeight: 800,
                    fontFamily: "'Outfit', sans-serif",
                    margin: 0,
                    color: '#ffffff'
                  }}>
                    ALUNO CONECTADO
                  </h1>
                </div>
              </div>

              {/* Status da Instrutora Alice */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                background: 'rgba(167, 139, 250, 0.08)',
                border: '1px solid rgba(167, 139, 250, 0.15)',
                padding: '8px 12px',
                borderRadius: '8px'
              }}>
                <GraduationCap size={16} style={{ color: '#a78bfa' }} />
                <span style={{ fontSize: '0.75rem', color: '#cbd5e1', fontWeight: 600 }}>Instrutora Alice</span>
                <span style={{ 
                  marginLeft: 'auto', 
                  width: '6px', 
                  height: '6px', 
                  borderRadius: '50%', 
                  background: '#10b981',
                  boxShadow: '0 0 8px #10b981'
                }} />
              </div>
            </div>

            {/* Listagem Hierárquica: Tópicos -> Subtópicos */}
            {effectivePerfil && (
              <TopicAccordion
                effectivePerfil={effectivePerfil}
                effectiveTopico={effectiveTopico}
                selectedSubtopico={selectedSubtopico}
                activeStyles={activeStyles}
                navigate={navigate}
              />
            )}
          </aside>

          {/* Área Principal do Vídeo Player */}
          <main style={{
            flex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              padding: '24px',
              boxSizing: 'border-box',
              gap: '16px',
              maxWidth: '1200px',
              width: '100%',
              margin: '0 auto',
              overflow: 'hidden'
            }}>
              {/* Barra de Controle de Roteiro Compacta acima do Vídeo */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                gap: '16px',
                flexShrink: 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                  <h2 style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 800, 
                    fontFamily: "'Outfit', sans-serif", 
                    lineHeight: 1.3,
                    margin: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {selectedSubtopico.titulo}
                  </h2>
                  <span style={{ 
                    fontSize: '0.8rem', 
                    color: '#94a3b8', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    background: 'rgba(255, 255, 255, 0.03)',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.05)'
                  }}>
                    <Clock size={13} style={{ color: activeStyles.color }} />
                    {selectedSubtopico.duracao}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <button
                    onClick={handlePrev}
                    disabled={!hasPrev}
                    className="action-btn"
                    style={{
                      width: '36px',
                      height: '36px',
                      background: hasPrev ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '10px',
                      color: hasPrev ? '#f8fafc' : '#64748b',
                      cursor: hasPrev ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: hasPrev ? 1 : 0.4
                    }}
                    title="Treinamento Anterior"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={!hasNext}
                    className="action-btn"
                    style={{
                      width: '36px',
                      height: '36px',
                      background: hasNext ? activeStyles.bgLight : 'rgba(255, 255, 255, 0.02)',
                      border: `1px solid ${hasNext ? activeStyles.borderLight : 'rgba(255, 255, 255, 0.08)'}`,
                      borderRadius: '10px',
                      color: hasNext ? '#f8fafc' : '#64748b',
                      cursor: hasNext ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: hasNext ? 1 : 0.4
                    }}
                    title="Próximo Treinamento"
                  >
                    <ChevronRight size={18} />
                  </button>

                  <div style={{ width: '1px', height: '18px', background: 'rgba(255, 255, 255, 0.12)', margin: '0 4px' }} />

                  <button
                    onClick={() => setShowDetailsModal(true)}
                    className="action-btn"
                    style={{
                      width: '36px',
                      height: '36px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '10px',
                      color: '#cbd5e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    title="Detalhes do Treinamento"
                  >
                    <Info size={18} style={{ color: activeStyles.color }} />
                  </button>

                  {selectedSubtopico.pdfUrl && (
                    <a 
                      href={selectedSubtopico.pdfUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="action-btn"
                      style={{
                        height: '36px',
                        background: activeStyles.bgLight,
                        border: `1px solid ${activeStyles.borderLight}`,
                        borderRadius: '10px',
                        color: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '0 12px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        textDecoration: 'none',
                        cursor: 'pointer'
                      }}
                      title="Manual (PDF) - Abre em nova aba"
                    >
                      <FileText size={18} style={{ color: activeStyles.color }} />
                      PDF
                    </a>
                  )}

                  {isInterno && (
                    <button
                      onClick={() => navigate('/ecadstudio')}
                      className="action-btn"
                      style={{
                        height: '36px',
                        background: 'rgba(139, 92, 246, 0.15)',
                        border: '1px solid rgba(139, 92, 246, 0.25)',
                        borderRadius: '10px',
                        color: '#c084fc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '0 12px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                      title="Abrir no Estúdio AI de Automação"
                    >
                      <Sparkles size={16} style={{ color: '#c084fc' }} />
                      Estúdio AI
                    </button>
                  )}
                </div>
              </div>

              {/* Player de Vídeo */}
              <div style={{
                flex: '1',
                minHeight: '200px',
                background: '#020617',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <video 
                  key={selectedSubtopico.id}
                  controls
                  style={{
                    width: '100%',
                    height: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: '16px'
                  }}
                  src={selectedSubtopico.videoUrl}
                  autoPlay
                >
                  Seu navegador não suporta a exibição de vídeos HTML5.
                </video>
              </div>
            </div>
          </main>
        </>
      ) : activePerfil && effectiveTopico ? (
        
        /* CASO 3: TELA DE SUBTÓPICOS DO TÓPICO SELECIONADO */
        <div style={{
          flex: 1,
          height: '100%',
          overflowY: 'auto',
          padding: '40px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}>
          <div style={{ maxWidth: '900px', width: '100%' }}>
            {/* Header com botão de voltar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '30px' }}>
              <button 
                onClick={() => navigate(`/ecad?perfil=${activePerfil.id}`)}
                className="back-btn"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '10px',
                  padding: '8px 12px',
                  color: '#cbd5e1',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}
              >
                <ArrowLeft size={16} />
                Voltar aos Tópicos
              </button>
              <div>
                <span style={{ fontSize: '0.75rem', color: activeStyles.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Perfil {activePerfil.nome} &bull; Tópico
                </span>
                <h1 style={{
                  fontSize: '1.8rem',
                  fontWeight: 800,
                  fontFamily: "'Outfit', sans-serif",
                  margin: 0,
                  background: 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {effectiveTopico.titulo}
                </h1>
              </div>
            </div>

            {/* Listagem de Subtópicos do Tópico Ativo */}
            {effectiveTopico.subtopicos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px', color: '#64748b' }}>
                Nenhum treinamento cadastrado neste tópico.
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '18px'
              }}>
                {(() => {
                  const topicIdx = activePerfil.topicos.findIndex(t => t.id === effectiveTopico.id);
                  const prefix = topicIdx >= 0 ? `${topicIdx + 1}.` : '';
                  return effectiveTopico.subtopicos.map((sub, sIdx) => (
                    <div 
                      key={sub.id}
                      onClick={() => navigate(`/ecad/${sub.id}?perfil=${activePerfil.id}&topico=${effectiveTopico.id}`)}
                      className="premium-card"
                      style={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        borderRadius: '16px',
                        padding: '20px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: '14px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <div>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#ffffff', margin: '0 0 8px 0', fontFamily: "'Outfit', sans-serif" }}>
                          {prefix}{sIdx + 1}. {sub.titulo}
                        </h3>
                      <p style={{ 
                        fontSize: '0.82rem', 
                        color: '#94a3b8', 
                        margin: 0, 
                        lineHeight: 1.45, 
                        display: '-webkit-box', 
                        WebkitLineClamp: 3, 
                        WebkitBoxOrient: 'vertical', 
                        overflow: 'hidden' 
                      }}>
                        {sub.descricao}
                      </p>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                      paddingTop: '12px',
                      fontSize: '0.72rem',
                      color: '#cbd5e1'
                    }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Clock size={11} style={{ color: activeStyles.color }} />
                          {sub.duracao}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Award size={11} style={{ color: activeStyles.color }} />
                          {sub.dificuldade}
                        </span>
                      </div>
                      <span style={{ color: activeStyles.color, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        Iniciar <ChevronRight size={12} />
                      </span>
                    </div>
                  </div>
                ));
              })()}
              </div>
            )}
          </div>
        </div>
      ) : activePerfil ? (
        
        /* CASO 2: TELA DE TÓPICOS DO PERFIL SELECIONADO */
        <div style={{
          flex: 1,
          height: '100%',
          overflowY: 'auto',
          padding: '40px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}>
          <div style={{ maxWidth: '900px', width: '100%' }}>
            {/* Header com botão de voltar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '30px' }}>
              <button 
                onClick={() => navigate('/ecad')}
                className="back-btn"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '10px',
                  padding: '8px 12px',
                  color: '#cbd5e1',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}
              >
                <ArrowLeft size={16} />
                Voltar aos Perfis
              </button>
              <div>
                <span style={{ fontSize: '0.75rem', color: activeStyles.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Área do Colaborador
                </span>
                <h1 style={{
                  fontSize: '1.8rem',
                  fontWeight: 800,
                  fontFamily: "'Outfit', sans-serif",
                  margin: 0,
                  background: 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Treinamentos para {activePerfil.nome}
                </h1>
              </div>
            </div>

            {/* Listagem de Tópicos do Perfil Ativo */}
            {activePerfil.topicos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px', color: '#64748b' }}>
                Nenhum tópico cadastrado para este perfil.
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '20px'
              }}>
                {activePerfil.topicos.map((topico) => (
                  <div 
                    key={topico.id}
                    onClick={() => navigate(`/ecad?perfil=${activePerfil.id}&topico=${topico.id}`)}
                    className="premium-card"
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      borderRadius: '16px',
                      padding: '24px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '44px',
                      height: '44px',
                      borderRadius: '10px',
                      background: activeStyles.bgLight,
                      color: activeStyles.color,
                      border: `1px solid ${activeStyles.borderLight}`
                    }}>
                      <BookOpen size={20} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#ffffff', margin: '0 0 4px 0', fontFamily: "'Outfit', sans-serif" }}>
                        {topico.titulo}
                      </h3>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                        {topico.subtopicos.length} roteiro{topico.subtopicos.length !== 1 ? 's' : ''} de treinamento
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                      paddingTop: '12px',
                      marginTop: '4px'
                    }}>
                      <span style={{ color: activeStyles.color, fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        Acessar Tópico <ChevronRight size={14} />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        
        /* CASO 1: TELA DE ENTRADA / SELETOR DE PERFIS (HOME) */
        <div style={{
          flex: 1,
          height: '100%',
          overflowY: 'auto',
          padding: '60px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}>
          <div style={{ maxWidth: '1000px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '40px' }}>
            
            {/* Header de Boas-vindas */}
            <div style={{ textAlign: 'center', maxWidth: '600px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'rgba(167, 139, 250, 0.1)',
                border: '1px solid rgba(167, 139, 250, 0.2)',
                color: '#a78bfa',
                marginBottom: '16px'
              }}>
                <BookOpen size={30} />
              </div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 900,
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: '-0.5px',
                margin: '0 0 10px 0',
                background: 'linear-gradient(135deg, #ffffff 0%, #a78bfa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                PORTAL DE CAPACITAÇÃO
              </h1>
              <p style={{ fontSize: '0.92rem', color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>
                Seja bem-vindo ao portal ECAD do Aluno Conectado. Selecione o seu perfil abaixo ou utilize a pesquisa para localizar tutoriais interativos e manuais oficiais.
              </p>
            </div>

            {/* Barra de Pesquisa Global */}
            <div style={{ position: 'relative', width: '100%', maxWidth: '540px' }}>
              <input 
                type="text"
                placeholder="Pesquisar treinamentos, tópicos ou manuais..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 18px 14px 46px',
                  borderRadius: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  background: 'rgba(15, 23, 42, 0.8)',
                  color: '#f8fafc',
                  fontSize: '0.95rem',
                  outline: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
              />
              <Search size={20} style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#64748b'
              }} />
            </div>

            {/* Resultados de Busca Global */}
            {searchQuery.trim().length > 0 ? (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#a78bfa', margin: 0 }}>
                    Resultados para "{searchQuery}"
                  </h3>
                  <button 
                    onClick={() => setSearchQuery('')}
                    style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.8rem' }}
                  >
                    Limpar
                  </button>
                </div>
                
                {searchResults.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', background: 'rgba(255,255,255,0.01)', border: '1px dashed rgba(255,255,255,0.06)', borderRadius: '14px', color: '#64748b' }}>
                    Nenhum treinamento encontrado com esses termos.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {searchResults.map(({ perfil, topico, subtopico }) => {
                      const profileStyles = getPerfilStyles(perfil.id);
                      return (
                        <div 
                          key={subtopico.id}
                          onClick={() => navigate(`/ecad/${subtopico.id}?perfil=${perfil.id}&topico=${topico.id}`)}
                          className="premium-card"
                          style={{
                            background: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            borderRadius: '12px',
                            padding: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <span style={{ fontSize: '0.65rem', color: profileStyles.color, fontWeight: 700, textTransform: 'uppercase' }}>
                              Perfil {perfil.nome} &bull; {topico.titulo}
                            </span>
                            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ffffff', margin: '2px 0 4px 0' }}>
                              {subtopico.titulo}
                            </h4>
                            <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                              {subtopico.descricao}
                            </p>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                            <span style={{ fontSize: '0.72rem', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <Clock size={12} style={{ color: profileStyles.color }} />
                              {subtopico.duracao}
                            </span>
                            <ChevronRight size={16} style={{ color: '#64748b' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              
              /* Grid de Seleção de Perfis */
              <div style={{ width: '100%' }}>
                <h3 style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#a78bfa',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  Escolha o seu Perfil de Acesso
                </h3>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '20px',
                  width: '100%'
                }}>
                  {perfis
                    .filter((p) => !p.isInterno) // Oculta o perfil interno na listagem geral
                    .map((perfil) => {
                      const profileStyles = getPerfilStyles(perfil.id);
                      return (
                        <div
                          key={perfil.id}
                          onClick={() => navigate(`/ecad?perfil=${perfil.id}`)}
                          className="premium-card"
                          style={{
                            background: 'rgba(255, 255, 255, 0.03)',
                            border: '1px solid rgba(255, 255, 255, 0.06)',
                            borderRadius: '16px',
                            padding: '24px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                            backdropFilter: 'blur(10px)',
                            boxSizing: 'border-box'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '46px',
                            height: '46px',
                            borderRadius: '12px',
                            background: profileStyles.bgLight,
                            border: `1px solid ${profileStyles.borderLight}`,
                            color: profileStyles.color
                          }}>
                            {profileStyles.icon}
                          </div>
                          <div>
                            <h4 style={{
                              fontSize: '1.1rem',
                              fontWeight: 700,
                              color: '#ffffff',
                              margin: '0 0 4px 0',
                              fontFamily: "'Outfit', sans-serif"
                            }}>
                              {perfil.nome}
                            </h4>
                            <p style={{
                              fontSize: '0.8rem',
                              color: '#94a3b8',
                              lineHeight: 1.4,
                              margin: 0
                            }}>
                              {profileStyles.desc}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Details Modal Dialog (Compartilhado) */}
      {effectivePerfil && (
        <VideoPlayerModal
          showDetailsModal={showDetailsModal}
          setShowDetailsModal={setShowDetailsModal}
          selectedSubtopico={selectedSubtopico}
          effectivePerfil={effectivePerfil}
          activeStyles={activeStyles}
        />
      )}
      </>
    </div>
  );
}

